import React, { useEffect, useState } from "react";
import {
  SectionCard,
  FormRow,
  FieldInput,
  PrimaryButton,
  SecondaryButton,
} from "@/components/ds";
import { ArrowLeft, Plus } from "@/components/ds/icons";
import useApi from "@/hooks/useApi";
import { useLocation, useNavigate } from "react-router-dom";
const SecurityLogo = "/img/velaro_security-safe-encryption.png";
const ShopifyLogo = "/img/shopify.svg";
const VelaroLogo = "/img/velaro.svg";
const Form1Logo = "/img/velaro_secure-form_1.svg";
const ArrowsLeftRight = "/img/arrows-left-right_1.svg";
import ShopifyPanel from "./ShopifyPanel";

interface Props {
  appStatus?: string;
}

export default function ShopifyCreate(_props: Props) {
  const [storeName, setStoreName] = useState("");
  const [domain, setDomain] = useState("");

  const [sending, setSending] = useState(false);
  const [, setSuccess] = useState<boolean | undefined>(undefined);
  const [configId, setConfigId] = useState<number>(-1);
  const [step, setStep] = useState(1);
  const [panels, setPanels] = useState<{ id: number; scopeType: string }[]>([]);
  const [, setShowSaved] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const api = useApi();
  const location = useLocation();
  const { teamId } = location.state || {};
  const navigate = useNavigate();
  const query = useQuery();

  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }

  useEffect(() => {
    if (query.get("error") === "true") {
      setOauthError(query.get("message") || "OAuth connection failed. Please try again.");
      return;
    }

    const shop = query.get("shop");
    if (!shop) return;

    setIsConnecting(false);
    setSending(false);
    setStep(3);

    // Recover the configId for this shop — React state is gone after the OAuth redirect
    api.messaging.get("Shopify/Integrations/").then(async (r) => {
      if (r.ok) {
        const configs = await r.json();
        if (Array.isArray(configs)) {
          const match = configs.find(
            (c: { domain: string; id: number }) => c.domain === shop
          );
          if (match) setConfigId(match.id);
        }
      }
    });
  }, [location.search, api.messaging]);

  const addPanel = () =>
    setPanels([...panels, { id: panels.length + 1, scopeType: "" }]);

  useEffect(() => {
    if (step === 3 && panels.length === 0) {
      addPanel();
    }
  }, [step]);

  async function handleSubmit() {
    setSending(true);
    try {
      const response = await api.messaging.post(
        "Shopify/Auth/SaveCredentials",
        {
          storeName,
          domain,
          teamId
        }
      );

      let result: any = {};
      if (response.ok) {
        result = await response.json();
      } else {
        console.error("Failed to save credentials:", response.status);
      }

      setSuccess(response.ok);
      if (response.ok && result.id) {
        setConfigId(result.id);
        setStep(step + 1);
      }
    } catch (err) {
      console.error("Error parsing response:", err);
      setSuccess(false);
    } finally {
      setSending(false);
    }
  }

  async function handleGrant() {
    if (!configId) {
      console.error("Config ID not set. Did you run handleSubmit first?");
      return;
    }

    setSending(true);
    try {
      const response = await api.messaging.get(`Shopify/authorize/${configId}`);
      const result = await response.json();
      setSuccess(response.ok);

      if (response.ok && result.authorizationUrl) {
        // Show loading animation while connecting to Shopify
        setIsConnecting(true);
        // Redirect user to Shopify authorization page
        window.location.href = result.authorizationUrl;
      }
    } catch (err) {
      console.error("Grant access failed", err);
      setSuccess(false);
    } finally {
      setSending(false);
    }
  }
  const showSavedMessage = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 3000);
  };

  const isNextActive = !!domain;

  return (
    <SectionCard title="Connect Shopify">
      {oauthError && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <strong>Connection failed:</strong> {oauthError}
        </div>
      )}

      <div className="flex items-center w-full mt-4">
        {[
          "Authentication",
          "Permission request",
          "Connection success",
          "Integration settings",
          "Integration complete"
        ].map((label, index) => {
          const isActive = step >= index + 1;
          const isCurrent = step === index + 1;
          const isCompleted = step > index + 1;
          return (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full border-2 z-10 ${
                    isActive ? "border-primary" : "border-zinc-300"
                  } bg-white`}
                >
                  <div
                    className={`w-6 h-6 rounded-full z-10 ${
                      isActive ? "bg-primary" : "bg-zinc-300"
                    }`}
                  ></div>
                </div>
                <p
                  className={`mt-2 font-medium text-sm ${
                    isCurrent
                      ? "text-primary"
                      : isCompleted
                      ? "text-zinc-800"
                      : "text-zinc-400"
                  }`}
                >
                  {label}
                </p>
              </div>
              {index < 4 && (
                <div
                  className={`flex-1 mb-8 -mx-[51px] z-0 border-zinc-300 ${
                    step > index + 1
                      ? "border-t-4 border-primary"
                      : step === index + 1
                      ? "border-t-4 border-primary border-dashed"
                      : "border-t-4 border-dashed"
                  }`}
                ></div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step 1 - Authentication */}
      {step === 1 && (
        <div className="w-1/3 place-self-center">
          <div className="w-full py-8 flex-col gap-6 inline-flex">
            <img className="w-40 place-self-center" src={Form1Logo} alt="" />
            <div className="text-center text-zinc-900 text-xl font-medium">
              Authentication key entry
            </div>
            <span className="text-center text-zinc-900 text-base font-normal">
              Enter your Shopify store URL to begin the OAuth connection.
            </span>

            <FormRow label="Shopify store name">
              <FieldInput
                value={storeName}
                placeholder="Enter Shopify store name"
                onChange={(e) => setStoreName(e.target.value)}
              />
            </FormRow>

            <FormRow label="Shopify store URL">
              <FieldInput
                value={domain}
                placeholder="yourstore.myshopify.com"
                onChange={(e) => setDomain(e.target.value)}
              />
            </FormRow>

            <div className="flex gap-2 justify-center mt-4">
              <SecondaryButton onClick={() => navigate("/integrations")}>
                Cancel
              </SecondaryButton>
              <PrimaryButton
                onClick={async () => {
                  if (isNextActive) {
                    await handleSubmit();
                  }
                }}
                disabled={!isNextActive || sending}
              >
                Create connection
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {/* Step 2 - Permission Request */}
      {step === 2 && (
        <div className="w-2/3 place-self-center text-center relative">
          <div className="w-full py-8 flex-col gap-6 inline-flex">
            <div className="flex justify-center gap-10">
              <img
                className="w-20 place-self-center"
                src={ShopifyLogo}
                alt=""
              />
              <img className="place-self-center" src={ArrowsLeftRight} alt="" />
              <img className="w-30 place-self-center" src={VelaroLogo} alt="" />
            </div>
            <div>
              <div className="text-center text-zinc-900 text-xl font-medium mb-6">
                Integration permission request
              </div>
              <div className="text-center text-zinc-800 text-md font-medium">
                Velaro wants access to:
              </div>

              <div className="grid grid-cols-2 gap-10 text-left mt-4">
                <div>
                  <div className="text-zinc-900 mb-6">
                    <div className="text-base font-medium">Customer profiles</div>
                    <div className="text-xs">
                      Access customer profiles including name, email, phone, and
                      address to display contact info inside Velaro.
                    </div>
                  </div>
                  <div className="text-zinc-900 mb-6">
                    <div className="text-base font-medium">Orders</div>
                    <div className="text-xs">
                      Retrieve order history, order status, and payment details
                      so agents can reference purchases during support.
                    </div>
                  </div>
                  <div className="text-zinc-900 mb-6">
                    <div className="text-base font-medium">Products</div>
                    <div className="text-xs">
                      Sync product catalog data (titles, SKUs, prices) to assist
                      with customer inquiries and recommendations.
                    </div>
                  </div>

                  <div className="text-zinc-900 mb-6">
                    <div className="text-base font-medium">Discounts</div>
                    <div className="text-xs">
                      Fetch discount codes and promotions to personalize
                      customer offers during chats.
                    </div>
                  </div>
                  <div className="text-zinc-900 mb-6">
                    <div className="text-base font-medium">Store settings</div>
                    <div className="text-xs">
                      Read basic store information (store name, currency, time
                      zone) for contextual display.
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-zinc-900 mb-6">
                    <div className="text-base font-medium">Inventory</div>
                    <div className="text-xs">
                      View stock levels and availability to answer customer
                      product questions in real time.
                    </div>
                  </div>

                  <div className="text-zinc-900 mb-6">
                    <div className="text-base font-medium">Checkouts</div>
                    <div className="text-xs">
                      Track checkout sessions for abandoned cart follow-up and
                      proactive engagement.
                    </div>
                  </div>

                  <div className="text-zinc-900 mb-6">
                    <div className="text-base font-medium">Fulfillment</div>
                    <div className="text-xs">
                      Access fulfillment details (shipping status, tracking
                      numbers) to provide delivery updates.
                    </div>
                  </div>

                  <div className="text-zinc-900 mb-6">
                    <div className="text-base font-medium">Draft orders</div>
                    <div className="text-xs">
                      Allow agents to create draft orders directly from Velaro
                      conversations for faster sales.
                    </div>
                  </div>

                  <div className="text-zinc-900 mb-6">
                    <div className="text-base font-medium">Reports</div>
                    <div className="text-xs">
                      Access analytics and reports for deeper insights into
                      sales and customer behavior.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-center">
              <SecondaryButton onClick={() => navigate("/integrations")}>
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={handleGrant}>
                Grant access
              </PrimaryButton>
            </div>
          </div>

          {isConnecting && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 z-50">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
              <div className="text-zinc-800 text-lg font-medium">
                Connecting to Shopify...
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3 - Connection Success */}
      {step === 3 && (
        <div className="w-2/3 place-self-center text-center">
          <div className="w-full py-8 flex-col gap-6 inline-flex">
            <img className="w-40 place-self-center" src={SecurityLogo} alt="" />
            <div className="text-center text-zinc-800 text-xl font-medium">
              Congratulations! Successful integration connection
              <p className="text-center text-zinc-800 text-sm font-normal">
                Enjoy a seamless flow of data between Velaro and Shopify. <br />
                Take the next step to tailor your integration by configuring
                settings.
              </p>
            </div>

            <div className="flex gap-2 justify-center">
              <SecondaryButton onClick={() => navigate("/integrations/")}>
                Finish later
              </SecondaryButton>
              <PrimaryButton onClick={() => setStep(step + 1)}>
                Complete setup
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {/* Step 4 - Integration Settings */}
      {step === 4 && (
        <div className="">
          <div className="w-full py-8 flex justify-between items-center">
            <div>
              <div className="text-zinc-800 text-base font-medium">
                Choose which records to sync
              </div>
              <div className="text-zinc-500 text-sm font-normal">
                Mappings connect individual fields with matching information
                from each app.
              </div>
            </div>
            <div className="flex gap-4">
              <SecondaryButton onClick={() => setStep(step + 1)}>
                Done
              </SecondaryButton>
              <PrimaryButton onClick={addPanel}>
                <span className="inline-flex items-center gap-1">
                  <Plus className="w-4 h-4" /> Add new mapping
                </span>
              </PrimaryButton>
            </div>
          </div>

          <div className="mt-4">
            {panels.map((panel) => (
              <ShopifyPanel
                key={panel.id}
                teamId={teamId!}
                scopeType={panel.scopeType}
                showSavedMessage={showSavedMessage}
                isCreateMode={true}
                selectedConfigId={configId}
                isDeleteDisabled={panels.length === 1}
              />
            ))}
          </div>
        </div>
      )}

      {/* Step 5 - Integration Complete */}
      {step === 5 && (
        <div className="w-2/3 place-self-center text-center">
          <div className="w-full py-8 flex-col gap-6 inline-flex">
            <img className="w-40 place-self-center" src={SecurityLogo} alt="" />
            <div className="text-center text-zinc-800 text-xl font-medium">
              Congratulations! Integration completed
            </div>
            <p className="text-center text-zinc-800 text-sm font-normal">
              Your Shopify store is connected and ready. <br />
              You can configure additional mappings or return to the dashboard.
            </p>

            <div className="flex gap-2 justify-center">
              <SecondaryButton onClick={() => navigate("/integrations/")}>
                Back to dashboard
              </SecondaryButton>
              <PrimaryButton
                onClick={() => (window.location.href = "/integrations/shopify")}
              >
                View details
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

export function ShopifyBackToShopifyOverview() {
  const navigate = useNavigate();
  return (
    <div className="relative flex items-center">
      <button
        type="button"
        className="flex cursor-pointer items-center gap-1 text-zinc-600"
        onClick={() => navigate("/integrations")}
      >
        <ArrowLeft className="h-5 w-5" />
        Back
      </button>
      <span className="absolute left-1/2 transform -translate-x-1/2 font-semibold text-md">
        Integrations
      </span>
    </div>
  );
}
